import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContentDropdownOpen, setIsContentDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'ABOUT', path: '/about' },
    { name: 'EVENTS', path: '/events' },
    { name: 'TEAM', path: '/team' },
    { name: 'PEOPLE', path: '/people' },
    { name: 'PARTNERSHIPS', path: '/partnerships' },
  ];

  const contentLinks = [
    { name: 'GALLERY', path: '/content/gallery' },
    { name: 'BLOG', path: '/content/blog' },
    { name: 'MADE, NOT MADE UP', path: '/content/made-not-made-up' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-black border-b border-white/10`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="nav-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_bf190b2c-0547-4061-a149-b09b814092c5/artifacts/xou2tw75_Screenshot%202026-05-07%20075825.png"
              alt="KAROBAR Logo"
              className="h-16 w-16 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Open Sans' }}>KAROBAR</span>
              <span className="text-xs text-gray-400 tracking-widest">THE ERA OF ENTREPRENEURS</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm tracking-wide ${
                  location.pathname === link.path ? 'active text-gold-500' : 'text-white hover:text-gold-500'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}-link`}
              >
                {link.name}
              </Link>
            ))}
            
            <div 
              className="relative"
              onMouseEnter={() => setIsContentDropdownOpen(true)}
              onMouseLeave={() => setIsContentDropdownOpen(false)}
            >
              <button 
                className="nav-link text-sm tracking-wide text-white hover:text-gold-500 flex items-center space-x-1"
                data-testid="nav-content-dropdown"
              >
                <span>CONTENT</span>
                <ChevronDown size={16} className={`transition-transform ${
                  isContentDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>
              
              {isContentDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-black/95 backdrop-blur-xl border border-white/10 shadow-xl">
                  {contentLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-6 py-3 text-sm text-white hover:text-gold-500 hover:bg-white/5 transition-colors"
                      data-testid={`content-${link.name.toLowerCase().replace(/[, ]/g, '-')}-link`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/join-us"
              className="btn-primary"
              data-testid="nav-join-us-button"
            >
              JOIN US
            </Link>
          </div>

          <button
            className="lg:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-base ${
                  location.pathname === link.path ? 'text-gold-500' : 'text-white hover:text-gold-500'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase()}-link`}
              >
                {link.name}
              </Link>
            ))}
            
            <div className="border-t border-white/10 pt-4">
              <p className="text-xs text-gray-400 mb-2 tracking-widest">CONTENT</p>
              {contentLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-base text-white hover:text-gold-500 py-2 pl-4"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-content-${link.name.toLowerCase().replace(/[, ]/g, '-')}-link`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            
            <Link
              to="/join-us"
              className="btn-primary block text-center"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-nav-join-us-button"
            >
              JOIN US
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;